import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { filterChildrenByType, getFlattenedChildren, NeverChildren } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion-9';
import { usePersistentNotifications } from '@tanium/react-persistent-notifications-context';
import { Stack } from '@tanium/react-stack';
import { css } from '@tanium/style-system';
import { ThemeShadows } from '@tanium/theme-data';
import { NOTIFICATION_COREUITYPE } from './Notification';
import { NotificationScrollerFill } from './themeDefinitions';

const NOTIFICATION_VISIBLE_HEIGHT = 136; // space for 3 single-line notifications
const SCROLL_PADDING_BOTTOM_PX = 16; // so shadow on last notification is visible

interface NotificationWrapperProps {
  isOverflowing: boolean;
}
interface NotificationScrollerProps {
  isOverflowing: boolean;
}

const NotificationWrapper = styled('div')<NotificationWrapperProps>(
  {
    marginBottom: 16,
    position: 'relative',
  },
  ({ isOverflowing }) =>
    isOverflowing &&
    css({
      paddingBottom: `${SCROLL_PADDING_BOTTOM_PX}px`,
      '::after': {
        content: `""`,
        position: 'absolute',
        bottom: 0,
        top: 0,
        left: 0,
        right: 0,
        boxShadow: ThemeShadows.OverflowVertical,
        pointerEvents: 'none',
      },
    }),
);

const NotificationScroller = styled('div')<NotificationScrollerProps>(({ isOverflowing }) =>
  css({
    backgroundColor: NotificationScrollerFill,
    // because Browser's overflow cuts off bottom shadow from the individual notification, this tricks it
    marginBottom: `${-SCROLL_PADDING_BOTTOM_PX}px`,
    paddingBottom: `${isOverflowing ? 0 : SCROLL_PADDING_BOTTOM_PX}px`,
    maxHeight: `${NOTIFICATION_VISIBLE_HEIGHT + (isOverflowing ? 0 : SCROLL_PADDING_BOTTOM_PX)}px`,
    overflowY: 'auto',
    height: '100%',
  }),
);

const NotificationContainerInner = ({ children }: { children?: React.ReactNode }) => {
  const wrapper = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const { current } = wrapper;
    const isContentOverflowing =
      !!current && current.scrollHeight > current.clientHeight + SCROLL_PADDING_BOTTOM_PX;
    setIsOverflowing(isContentOverflowing);
  }, [children]);

  return (
    <NotificationWrapper isOverflowing={isOverflowing}>
      <NotificationScroller isOverflowing={isOverflowing} innerRef={wrapper}>
        {children}
      </NotificationScroller>
    </NotificationWrapper>
  );
};

export const NotificationContainer: FC<NeverChildren> = () => {
  const { notifications: notificationsFromContext } = usePersistentNotifications();

  const notifications = filterChildrenByType(
    getFlattenedChildren(notificationsFromContext),
    NOTIFICATION_COREUITYPE,
    'Workbench',
  );

  return (
    <>
      {notifications && notifications.length > 0 && (
        <Stack direction="column" itemSpacing={1} component={NotificationContainerInner}>
          {notifications}
        </Stack>
      )}
    </>
  );
};
